/**
 * src/components/FiltersBar.tsx — reusable search + date-range filter strip.
 *
 * v0.4.9.2.3c22ca: standardizes the filter UI across all admin pages and
 * the BOLO history views. Used at:
 *   - admin records tabs (Most Wanted, Missing Persons, Stolen Vehicles)
 *   - BoloTab history list
 *   - civilRespond.tsx history section
 *   - civilRespondBoloHistory.tsx (the dedicated /bolo-history page)
 *
 * The component is purely controlled — it emits search + date-range via
 * callbacks; each consumer applies the filter to its own data shape.
 * This keeps the component reusable across records, bolos, etc.
 *
 * Visual:
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │ 🔍 [Search records…              ] [×]                          │
 *   │  Presets: [7d] [14d] [30d] [Custom]                             │
 *   │  Custom range (when Custom is selected): [start] [→] [end]      │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * Defaults:
 *   - Search: empty (no filter)
 *   - Date range: last 7 days (the locked requirement per c22c spec)
 *
 * Date semantics:
 *   - DateRange.start / .end are Date objects (start of day, end of day).
 *   - When both are null, no date filter is applied (= "All time").
 *   - Presets reset the range to the most recent N days (rolling).
 *   - "Custom" lets the user pick explicit dates; switching away from
 *     "Custom" resets to the last 7 days so the user doesn't accidentally
 *     keep an old custom range they forgot about.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, X, Search, Check } from 'lucide-react';

export interface DateRange {
  /** Inclusive lower bound (start of day). null = no lower bound. */
  start: Date | null;
  /** Inclusive upper bound (end of day). null = no upper bound. */
  end: Date | null;
}

export interface FiltersBarProps {
  searchValue: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder?: string;
  /** ISO YYYY-MM-DD helpers so callers can serialize if they need to. */
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  /** "last 7 days" / "last 14 days" etc. Default '7d'. */
  initialPresetDays?: 7 | 14 | 30 | null;
}

type PresetDays = 7 | 14 | 30;

const PRESETS: Array<{ label: string; days: PresetDays }> = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
];

function rangeForLastDays(days: PresetDays): DateRange {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const start = new Date(now);
  start.setDate(start.getDate() - (days - 1));
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

function isoDate(d: Date | null): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function parseIsoDate(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

/**
 * Parse a YYYY-MM-DD string as the END of that local day (23:59:59.999).
 * Without this, picking an end-date would create a range that excludes
 * almost everything that day — e.g. end=2026-09-25 with midnight would
 * exclude records created at 00:01..23:59 on Sep 25 local time.
 *
 * All comparisons use local Date objects internally; timestamps stored
 * on records (typically ISO UTC strings from the DB) are converted via
 * `new Date(ts)` which gives a correct epoch regardless of source TZ.
 */
function parseIsoDateEnd(s: string): Date | null {
  const start = parseIsoDate(s);
  if (!start) return null;
  start.setHours(23, 59, 59, 999);
  return start;
}

function dateRangesEqual(a: DateRange, b: DateRange): boolean {
  return (
    (a.start?.getTime() ?? null) === (b.start?.getTime() ?? null) &&
    (a.end?.getTime() ?? null) === (b.end?.getTime() ?? null)
  );
}

export function FiltersBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  dateRange,
  onDateRangeChange,
  initialPresetDays = 7,
}: FiltersBarProps) {
  const { t } = useTranslation();
  // Track which preset is "active" for the button-highlight state. null = custom.
  const matched = PRESETS.find(p => dateRangesEqual(dateRange, rangeForLastDays(p.days)));
  const activePreset: PresetDays | null = matched ? matched.days : null;
  const [isCustom, setIsCustom] = useState(activePreset === null && initialPresetDays === null);

  function applyPreset(days: PresetDays) {
    setIsCustom(false);
    onDateRangeChange(rangeForLastDays(days));
  }

  function openCustom() {
    setIsCustom(true);
    // Don't clear the range — let the user adjust the existing one.
    // Initialize if it's still empty.
    if (!dateRange.start && !dateRange.end) {
      onDateRangeChange(rangeForLastDays(7));
    }
  }

  function setStart(s: string) {
    onDateRangeChange({ start: parseIsoDate(s), end: dateRange.end });
  }
  function setEnd(s: string) {
    // v0.4.9.2.3c22ca fix: end-date must include the full local day,
    // not just the midnight instant. Otherwise records created later
    // that day (in local time) are incorrectly filtered out.
    onDateRangeChange({ start: dateRange.start, end: parseIsoDateEnd(s) });
  }

  function clearAll() {
    setIsCustom(false);
    onSearchChange('');
    if (initialPresetDays) {
      onDateRangeChange(rangeForLastDays(initialPresetDays));
    } else {
      onDateRangeChange({ start: null, end: null });
    }
  }

  return (
    <div className="bg-card border border-border rounded-lg p-3 mb-3 space-y-2">
      {/* Search row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchValue}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-8 pr-8 py-2 border border-input rounded text-sm bg-background focus:outline-none focus:border-primary"
          />
          {searchValue && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                  title={t('filters.clearSearch')}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Date preset row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{t('filters.label')}</span>
        {PRESETS.map(p => {
          const active = activePreset === p.days;
          return (
            <button
              key={p.days}
              type="button"
              onClick={() => applyPreset(p.days)}
              className={`px-2 py-1 text-xs rounded border transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-input hover:bg-accent'
              }`}
            >
              {t('filters.last')} {p.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={openCustom}
          className={`px-2 py-1 text-xs rounded border transition-colors flex items-center gap-1 ${
            isCustom
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-foreground border-input hover:bg-accent'
          }`}
        >
          {isCustom && <Check className="w-3 h-3" />}
          {t('filters.custom')}
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="px-2 py-1 text-xs text-muted-foreground hover:text-foreground ml-auto"
          title={t('filters.resetAllTitle')}
        >
          {t('filters.reset')}
        </button>
      </div>

      {/* Custom date range row (only when Custom is selected) */}
      {isCustom && (
        <div className="flex items-center gap-2 flex-wrap pl-6">
              <label className="text-xs text-muted-foreground">{t('filters.from')}</label>
          <input
            type="date"
            value={isoDate(dateRange.start)}
            onChange={e => setStart(e.target.value)}
            max={isoDate(dateRange.end) || undefined}
            className="px-2 py-1 border border-input rounded text-xs bg-background"
          />
          <span className="text-xs text-muted-foreground">→</span>
              <label className="text-xs text-muted-foreground">{t('filters.to')}</label>
          <input
            type="date"
            value={isoDate(dateRange.end)}
            onChange={e => setEnd(e.target.value)}
            min={isoDate(dateRange.start) || undefined}
            className="px-2 py-1 border border-input rounded text-xs bg-background"
          />
        </div>
      )}
    </div>
  );
}

/**
 * Helper: apply the FiltersBar state to a list of records.
 * Pass `getCreatedAt(record)` to extract the timestamp per record shape.
 *
 * - Records outside the date range are filtered out.
 * - Records whose text-field(s) don't include `search` (case-insensitive)
 *   are filtered out. If search is empty, all (date-filtered) records pass.
 */
export function applyFilters<T>(
  records: T[],
  search: string,
  dateRange: DateRange,
  getCreatedAt: (r: T) => string | null | undefined,
  getSearchableText: (r: T) => string,
): T[] {
  const q = search.trim().toLowerCase();
  return records.filter(r => {
    // Date check
    if (dateRange.start || dateRange.end) {
      const ts = getCreatedAt(r);
      if (!ts) return false;
      const dt = new Date(ts);
      if (Number.isNaN(dt.getTime())) return false;
      if (dateRange.start && dt < dateRange.start) return false;
      if (dateRange.end && dt > dateRange.end) return false;
    }
    // Search check
    if (q) {
      const haystack = getSearchableText(r).toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}
